import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '../api/client';
import Modal from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';
import { StatusBadge } from './Dashboard';
import { CheckCircle2, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

export default function Approvals() {
  const { user } = useAuth();
  // Supervisors get the filterable team-history view; admins keep the original pending queue.
  return user?.role === 'supervisor' ? <SupervisorApprovals /> : <AdminApprovals />;
}

const STATUSES = ['all', 'pending', 'approved', 'rejected', 'out', 'closed', 'cancelled'];

function SupervisorApprovals() {
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get('status');
  // Sidebar link has no param → Pending. Dashboard "View all" passes ?status=all.
  const status = STATUSES.includes(param) ? param : 'pending';

  const [passes, setPasses] = useState([]);
  const [team, setTeam] = useState([]);
  const [employee, setEmployee] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      // Server scopes every request to this supervisor's team. 'all' is a UI-only value —
      // the API expects the status param to be omitted, never the literal string 'all'.
      const { data } = await api.get('/gate-passes', { params: status === 'all' ? {} : { status } });
      setPasses(data);
    } catch {}
    setLoaded(true);
  };

  useEffect(() => {
    setLoaded(false);
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [status]);

  useEffect(() => {
    api.get('/users/my-team').then(({ data }) => setTeam(data)).catch(() => {});
  }, []);

  const setStatus = (s) => {
    // Keep the sidebar's clean /approvals URL for the Pending default.
    setSearchParams(s === 'pending' ? {} : { status: s }, { replace: true });
  };

  // Narrows the already team-scoped result set; it can never widen it.
  const filtered = employee ? passes.filter((p) => p.requested_by === employee) : passes;

  const fmt = (iso) => (iso ? format(new Date(iso), 'dd MMM yyyy, HH:mm') : '—');

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <header>
        <div className="overline">Module 06</div>
        <h1 className="heading text-3xl font-black text-ink">Approvals</h1>
        <p className="text-sm text-zinc-500 mt-1">
          {filtered.length} pass{filtered.length !== 1 ? 'es' : ''}
          {status === 'pending' ? ' awaiting your signature.' : ` · ${status === 'all' ? 'all statuses' : status}`}
        </p>
      </header>

      {/* Filters */}
      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <div className="flex gap-1 flex-wrap flex-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`filter-${s}`}
              className={`text-xs font-bold uppercase px-3 py-2 rounded-sm border ${
                status === s ? 'bg-ink text-white border-ink' : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <select
          className="input max-w-[220px]"
          value={employee}
          onChange={(e) => setEmployee(e.target.value)}
          data-testid="filter-employee"
        >
          <option value="">All employees</option>
          {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
                <th className="px-4 py-3 overline">Employee</th>
                <th className="px-4 py-3 overline">Pass Type</th>
                <th className="px-4 py-3 overline">Status</th>
                <th className="px-4 py-3 overline">Requested At</th>
                <th className="px-4 py-3 overline">Actioned At</th>
                <th className="px-4 py-3 overline">Actioned By</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="approvals-table-body">
              {loaded && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-zinc-400">
                    {status === 'pending' && !employee ? (
                      <>
                        <CheckCircle2 className="mx-auto text-green-500 mb-2" size={32} />
                        Queue clear — all requests have been processed.
                      </>
                    ) : 'No gate passes match these filters.'}
                  </td>
                </tr>
              )}
              {filtered.map((p) => {
                const adminOverride = p.approved_by && p.supervisor_id && p.approved_by !== p.supervisor_id;
                return (
                  <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`approval-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-ink">{p.employee_name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{p.pass_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={p.pass_type === 'official' ? 'badge-info' : 'badge-muted'}>{p.pass_type}</span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{fmt(p.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{fmt(p.approved_at)}</td>
                    <td className="px-4 py-3 text-xs">
                      {p.approved_by_name ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-zinc-700">{p.approved_by_name}</span>
                          {adminOverride && <span className="badge-warn">Admin override</span>}
                        </div>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {p.status === 'pending' && (
                        <div className="flex gap-2 justify-end">
                          <button className="btn-success text-xs" onClick={() => setSelected({ pass: p, action: 'approve' })} data-testid={`approve-${p.id}`}>
                            <CheckCircle2 size={12} /> Approve
                          </button>
                          <button className="btn-danger text-xs" onClick={() => setSelected({ pass: p, action: 'reject' })} data-testid={`reject-${p.id}`}>
                            <XCircle size={12} /> Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <ActionModal
          pass={selected.pass}
          action={selected.action}
          onClose={() => setSelected(null)}
          onDone={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function AdminApprovals() {
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get('/gate-passes?status=pending');
    setPending(data);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <header>
        <div className="overline">Module 06</div>
        <h1 className="heading text-3xl font-black text-ink">Pending Approvals</h1>
        <p className="text-sm text-zinc-500 mt-1">{pending.length} pass{pending.length !== 1 ? 'es' : ''} awaiting your signature.</p>
      </header>

      {pending.length === 0 ? (
        <div className="card p-16 text-center">
          <CheckCircle2 className="mx-auto text-green-500 mb-3" size={48} />
          <h3 className="heading font-bold text-xl text-ink">Queue clear</h3>
          <p className="text-sm text-zinc-500 mt-1">All requests have been processed.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pending.map((p) => (
            <div key={p.id} className="card p-4 border-l-4 border-l-yellow-400 card-hover" data-testid={`approval-${p.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-bold text-ink text-lg">{p.employee_name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{p.pass_id}</div>
                </div>
                <div className="flex items-center gap-1 text-xs text-zinc-500">
                  <Clock size={12} />
                  {p.created_at && formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap my-3">
                <span className={p.pass_type === 'official' ? 'badge-info' : 'badge-muted'}>{p.pass_type}</span>
                <span className="badge-muted">{p.department}</span>
                <span className={p.will_return ? 'badge-success' : 'badge-warn'}>{p.will_return ? 'Will Return' : 'No Return'}</span>
              </div>
              <div className="text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-sm p-3 my-3">
                <div className="overline mb-1">Reason</div>
                {p.reason}
              </div>
              <div className="flex gap-2">
                <button className="btn-success flex-1" onClick={() => setSelected({ pass: p, action: 'approve' })} data-testid={`approve-${p.id}`}>
                  <CheckCircle2 size={14} /> Approve
                </button>
                <button className="btn-danger flex-1" onClick={() => setSelected({ pass: p, action: 'reject' })} data-testid={`reject-${p.id}`}>
                  <XCircle size={14} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <ActionModal
          pass={selected.pass}
          action={selected.action}
          onClose={() => setSelected(null)}
          onDone={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function ActionModal({ pass, action, onClose, onDone }) {
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (action === 'reject' && !remarks.trim()) {
      toast.error('Please add a reason for rejection');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/gate-passes/${pass.id}/approve`, { action, remarks });
      toast.success(action === 'approve' ? 'Pass approved' : 'Pass rejected');
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, e?.response?.data));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={action === 'approve' ? 'Approve Gate Pass' : 'Reject Gate Pass'}
      subtitle={pass.pass_id} size="sm"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className={action === 'approve' ? 'btn-success' : 'btn-danger'} disabled={loading} onClick={submit} data-testid="confirm-action">
          Confirm {action}
        </button>
      </>}
    >
      <div className="space-y-3">
        <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-sm text-sm">
          <div className="font-bold text-ink">{pass.employee_name}</div>
          <div className="text-xs text-zinc-500 mb-1">{pass.department} · {pass.pass_type}</div>
          <div className="text-sm">{pass.reason}</div>
        </div>
        <div>
          <label className="label">Remarks {action === 'reject' && '*'}</label>
          <textarea className="input" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="approval-remarks"
            placeholder={action === 'reject' ? 'Reason for rejection' : 'Optional remarks'}
          />
        </div>
      </div>
    </Modal>
  );
}
